import React, { useEffect, useState, useRef } from "react";
import { X } from "lucide-react";
import { accessoryService } from "../../../services/accessoryService";
import { API_BASE_URL } from "../../../config/api";

const AccessoryTab = ({ productId }) => {
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Multi-code input state
  const [codeInput, setCodeInput] = useState("");
  const [pendingCodes, setPendingCodes] = useState([]); // [{code, name, image}]
  const [suggestions, setSuggestions] = useState([]);
  const [allAccessoryProducts, setAllAccessoryProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [codeError, setCodeError] = useState("");
  const dropdownRef = useRef(null);

  /* ---------------- FETCH ACCESSORIES FOR THIS PRODUCT ---------------- */
  useEffect(() => {
    if (productId) fetchAccessories();
  }, [productId]);

  const fetchAccessories = async () => {
    setLoading(true);
    try {
      const data = await accessoryService.getAccessoriesByProduct(productId);
      setAccessories(data);
    } catch (err) {
      console.error("Failed to fetch accessories:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- FETCH ACCESSORY-CATEGORY PRODUCTS ---------------- */
  useEffect(() => {
    const fetchAccessoryProducts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/products/getallproducts`);
        const data = await res.json();
        const filtered = data.filter(
          (p) =>
            !p.isDeleted &&
            (p.categoryName?.toLowerCase() === "accessories" ||
              p.categoryName?.toLowerCase() === "accessory")
        );
        setAllAccessoryProducts(filtered);
      } catch (err) {
        console.error("Failed to fetch accessory category products:", err);
      }
    };
    fetchAccessoryProducts();
  }, []);

  /* ---------------- AUTOCOMPLETE LOGIC ---------------- */
  const handleCodeInputChange = (e) => {
    const val = e.target.value;
    setCodeInput(val);
    setCodeError("");

    if (val.trim().length > 0) {
      const filtered = allAccessoryProducts.filter(
        (p) =>
          p.productCode?.toLowerCase().includes(val.toLowerCase()) ||
          p.productName?.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(filtered);
      setShowDropdown(true);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelectSuggestion = (product) => {
    // Check if already in pending list
    if (pendingCodes.find((p) => p.code === product.productCode)) {
      setCodeError(`Code "${product.productCode}" already added`);
      setShowDropdown(false);
      setCodeInput("");
      return;
    }
    // Check if already saved as an accessory
    if (accessories.find((a) => a.linkedProductCode === product.productCode)) {
      setCodeError(`Code "${product.productCode}" is already an accessory`);
      setShowDropdown(false);
      setCodeInput("");
      return;
    }
    setPendingCodes((prev) => [
      ...prev,
      {
        code: product.productCode,
        name: product.productName,
        image: product.images?.[0] || null,
      },
    ]);
    setCodeInput("");
    setSuggestions([]);
    setShowDropdown(false);
    setCodeError("");
  };

  const handleAddByCode = () => {
    const code = codeInput.trim();
    if (!code) return;
    const found = allAccessoryProducts.find(
      (p) => p.productCode?.toLowerCase() === code.toLowerCase()
    );
    if (!found) {
      setCodeError(
        `Product code "${code}" not found in the Accessory category`
      );
      return;
    }
    handleSelectSuggestion(found);
  };

  const removePending = (code) => {
    setPendingCodes((prev) => prev.filter((p) => p.code !== code));
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- SAVE ALL PENDING ACCESSORIES ---------------- */
  const handleSaveAll = async () => {
    if (pendingCodes.length === 0) return;

    setUploading(true);
    let saved = 0;
    let failed = 0;
    for (const item of pendingCodes) {
      try {
        const formData = new FormData();
        const idStr = typeof productId === 'object' ? (productId?._id || productId?.id || productId) : productId;
        formData.append("productId", String(idStr));
        formData.append("linkedProductCode", item.code);
        await accessoryService.createAccessory(formData);
        saved++;
      } catch (err) {
        console.error(`Failed to save ${item.code}:`, err);
        failed++;
      }
    }
    setUploading(false);
    setPendingCodes([]);
    fetchAccessories();
    if (failed > 0) {
      alert(`${saved} accessory/ies saved. ${failed} failed.`);
    }
  };

  /* ---------------- DELETE ACCESSORY ---------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete accessory?")) return;
    try {
      await accessoryService.deleteAccessory(id);
      setAccessories((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete accessory");
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">
        Accessories (Complete Your Look)
      </h3>

      {/* SAVED ACCESSORIES LIST */}
      <div className="grid grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-sm text-gray-400">Loading...</div>
        ) : accessories.length === 0 ? (
          <div className="text-sm text-gray-500 col-span-3">
            No accessories linked yet. Add product codes below.
          </div>
        ) : (
          accessories.map((acc) => (
            <div
              key={acc._id}
              className="border rounded-md p-2 flex flex-col gap-1"
            >
              {acc.images?.[0] && (
                <img
                  src={acc.images[0]}
                  alt={acc.name}
                  className="w-full h-28 object-cover rounded"
                />
              )}
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm font-medium truncate">{acc.name}</span>
                <button
                  onClick={() => handleDelete(acc._id)}
                  className="text-red-500 text-sm ml-1 shrink-0"
                >
                  Delete
                </button>
              </div>
              {acc.linkedProductCode && (
                <span className="text-xs text-gray-500">
                  Code: {acc.linkedProductCode}
                </span>
              )}
            </div>
          ))
        )}
      </div>

      {/* ADD ACCESSORY FORM */}
      <div className="pt-4 border-t">
        <h4 className="text-sm font-semibold mb-1 text-gray-800">
          Add Accessory by Product Code
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Only products in the <span className="font-medium text-purple-600">Accessory</span> category can be added.
          Type a code or name to search.
        </p>

        {/* Code Input with Autocomplete */}
        <div className="flex gap-2 items-start mb-3" ref={dropdownRef}>
          <div className="relative flex-1">
            <input
              value={codeInput}
              onChange={handleCodeInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (suggestions.length === 1) {
                    handleSelectSuggestion(suggestions[0]);
                  } else {
                    handleAddByCode();
                  }
                }
              }}
              placeholder="Search by product code or name..."
              className="w-full px-3 py-2 border rounded shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {showDropdown && suggestions.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-52 overflow-y-auto">
                {suggestions.map((p) => (
                  <button
                    key={p._id}
                    onMouseDown={() => handleSelectSuggestion(p)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-purple-50 text-left border-b last:border-0"
                  >
                    {p.images?.[0] && (
                      <img
                        src={p.images[0]}
                        alt={p.productName}
                        className="w-8 h-8 object-cover rounded"
                      />
                    )}
                    <div>
                      <div className="text-sm font-medium">{p.productName}</div>
                      <div className="text-xs text-gray-400">
                        Code: {p.productCode}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showDropdown && suggestions.length === 0 && codeInput.trim() && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg px-3 py-2 text-sm text-red-500">
                No accessory-category products match "{codeInput}"
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddByCode}
            className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded hover:bg-purple-200 text-sm"
          >
            Add
          </button>
        </div>

        {codeError && (
          <p className="text-xs text-red-500 mb-2">{codeError}</p>
        )}

        {/* Pending Codes Chips */}
        {pendingCodes.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">
              Queued ({pendingCodes.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {pendingCodes.map((item) => (
                <div
                  key={item.code}
                  className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-3 py-1"
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-5 h-5 object-cover rounded-full"
                    />
                  )}
                  <span className="text-xs font-medium text-purple-700">
                    {item.name}
                  </span>
                  <span className="text-xs text-gray-400">({item.code})</span>
                  <button
                    onClick={() => removePending(item.code)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        {pendingCodes.length > 0 && (
          <button
            onClick={handleSaveAll}
            disabled={uploading}
            className="px-5 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
          >
            {uploading
              ? "Saving..."
              : `Save ${pendingCodes.length} Accessor${pendingCodes.length > 1 ? "ies" : "y"}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default AccessoryTab;
